import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, ArrowUpRightIcon, MailIcon, StarIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { PILLARS, PARTICIPATION, JOIN_STEPS, PRESIDENT_LETTER } from '../data/national-network-content';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/eZ5rmta1LUT5zNjEpkMWnd/HAA_National_Network.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const NAVY_DEEP = '#071427';
const BLUE = '#1b5fbe';
const SKY = '#3FA9F5';
const RED = '#e0343c';
const PALE = '#eef3fa';
const EASE = [0.16, 1, 0.3, 1] as const;
function SectionLabel({
  children,
  onDark = false
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{
    color: onDark ? SKY : BLUE
  }} data-id="element-3786" data-mp="2lwu1abzhgpcy">
      <StarIcon className="w-3 h-3" style={{
      color: RED
    }} fill={RED} data-id="element-3787" data-mp="2c7k78ql2wwm7" />
      {children}
    </p>;
}
export function NationalNetworkPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  const joinHref = mailto(isEs ? 'Solicitud de Membresía — Red Nacional Hondureño-Americana' : 'Membership Application — Honduran-American National Network');
  return <div className="min-h-screen w-full font-nunito antialiased" style={{
    backgroundColor: '#ffffff',
    color: NAVY
  }} data-id="element-3788" data-mp="3nn8tffqidrly">
      {/* Header — white bar carrying the National Network lockup */}
      <header className="sticky top-0 z-30 w-full bg-white shadow-sm" data-id="element-3789" data-mp="1gz8k40l27p7x">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4" data-id="element-3790" data-mp="1gwvtkz18k6j9">
          <a href="#" className="flex items-center group" aria-label="Honduran American Association National Network" data-id="element-3791" data-mp="1lrrt0k8sxw6e">
            <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-11 sm:h-14 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3792" data-mp="1c0owzwuqc7dz" />
          </a>
          <div className="flex items-center gap-4" data-id="element-3793" data-mp="1qos1t2x1dd17">
            <div className="inline-flex rounded-full border overflow-hidden" style={{
            borderColor: 'rgba(11,31,63,0.25)'
          }} role="group" aria-label={isEs ? 'Idioma' : 'Language'} data-id="element-3794" data-mp="1qo08xzutptem">
              {(['EN', 'ES'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} className="px-3.5 py-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: language === lang ? NAVY : 'transparent',
              color: language === lang ? '#ffffff' : 'rgba(11,31,63,0.6)'
            }} data-id="element-3795" data-mp="kaammalxdd72">
                  {lang}
                </button>)}
            </div>
            <a href={joinHref} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
            backgroundColor: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = NAVY;
          }} data-id="element-3796" data-mp="3k5fpuwk238rf">
              {isEs ? 'Únase' : 'Join'}
            </a>
            <a href="#" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-60" style={{
            color: NAVY
          }} data-id="element-3797" data-mp="29pifox558tnv">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3798" data-mp="2ij55m6mo72p5" />
              {isEs ? 'Volver' : 'Back'}
            </a>
          </div>
        </div>
        <div className="h-[3px] w-full flex" data-id="element-3799" data-mp="2seozknhobrb9">
          <div className="flex-1" style={{
          backgroundColor: BLUE
        }} data-id="element-3800" data-mp="2sddp64tq3lb6" />
          <div className="flex-1 bg-white" data-id="element-3801" data-mp="2xfcsxftdkk8n" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3802" data-mp="2xcgtto5sjpim" />
        </div>
      </header>

      <main data-id="element-3803" data-mp="31dk9a9jxfjjb">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-3804" data-mp="1312251t7zg08">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.13]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)'
        }} data-id="element-3805" data-mp="172nb4c2mva55" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20 text-center" data-id="element-3806" data-mp="1dinhjhef83jj">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-3807" data-mp="19epif6p3isqh">
              <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.02] mb-8" data-id="element-3808" data-mp="3u8ch67k7ba44">
                <span className="block" style={{
                color: SKY
              }} data-id="element-3809" data-mp="3p6ddewkjub6n">
                  {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
                </span>
                <span className="block text-white" data-id="element-3810" data-mp="3vlu9p7cxhkhk">
                  {isEs ? 'Mayor Conexión' : 'Greater Connection'}
                </span>
                <span className="block" style={{
                color: RED
              }} data-id="element-3811" data-mp="3lozf2zjs048k">
                  {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
                </span>
              </h1>
              <p className="font-nunito text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9" data-id="element-3812" data-mp="3cost0pa0hvxs">
                {isEs ? 'Un espacio nacional que conecta a las organizaciones, líderes y socios que ya sirven a las comunidades hondureñas en los Estados Unidos y en Honduras.' : 'A national space connecting the organizations, leaders, and partners already serving Honduran communities in the United States and in Honduras.'}
              </p>
              <a href={joinHref} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = NAVY;
            }} data-id="element-3813" data-mp="2u0e1m0vs8fwg">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3814" data-mp="333jeyglunrab" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Logo band */}
        <section className="bg-white" data-id="element-3815" data-mp="373tno1110vsv">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 lg:py-7 flex justify-center" data-id="element-3816" data-mp="374v8gx7ugwns">
            <motion.img initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} src="https://cdn.magicpatterns.com/uploads/xp6mxLQyMd62ZaHp36n7MC/HAA_National_Network.png" alt="Honduran American Association National Network" className="w-[clamp(18rem,58vw,44rem)] h-auto object-contain" data-id="element-3817" data-mp="i9lmrf7zb0ul" />
          </div>
        </section>

        {/* 2 — Letter from the President */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3818" data-mp="8j909hglsxlt">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3819" data-mp="8ih7eeee5dz8">
            <SectionLabel data-id="element-3820" data-mp="dcbn76qvl8t1">
              {isEs ? 'Carta del Presidente' : 'Letter from the President'}
            </SectionLabel>
            <p className="font-bebas text-3xl tracking-[0.04em] mb-7" style={{
            color: BLUE
          }} data-id="element-3821" data-mp="3h03te57ln3mp">
              {isEs ? PRESIDENT_LETTER.salutationEs : PRESIDENT_LETTER.salutation}
            </p>
            <div className="space-y-5 font-nunito text-base sm:text-[1.0625rem] leading-relaxed" style={{
            color: 'rgba(11,31,63,0.78)'
          }} data-id="element-3822" data-mp="34iavc3t47f1l">
              {(isEs ? PRESIDENT_LETTER.paragraphsEs : PRESIDENT_LETTER.paragraphs).map(p => <p key={p.slice(0, 40)} data-id="element-3823" data-mp="2lr07xwmllkeg">{p}</p>)}
            </div>
            <div className="mt-10 pt-7 border-t" style={{
            borderColor: 'rgba(11,31,63,0.15)'
          }} data-id="element-3824" data-mp="2lox86kvn8cwj">
              <p className="font-nunito text-sm mb-7" style={{
              color: 'rgba(11,31,63,0.6)'
            }} data-id="element-3825" data-mp="2btcct5l2yavr">
                {isEs ? PRESIDENT_LETTER.closingEs : PRESIDENT_LETTER.closing}
              </p>
              <div className="grid sm:grid-cols-2 gap-8 sm:gap-6" data-id="element-3826" data-mp="22snzkd3igejx">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/mcPsQzGFDU6t8Dm21iMLQ5/154111606_32s61fjkqp-GREY.jpg',
                name: PRESIDENT_LETTER.name,
                role: isEs ? PRESIDENT_LETTER.roleEs : PRESIDENT_LETTER.role,
                position: '50% 22%'
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/kb8rqEBdvwMudH3CyTb87Y/ECallejas.jpg',
                name: 'Eva Callejas',
                role: isEs ? 'Vicepresidenta de Alianzas e Iniciativas Estratégicas' : 'Vice President of Partnerships & Strategic Initiatives',
                position: '50% 18%'
              }].map(person => <div key={person.name} className="flex items-center gap-5 sm:gap-6" data-id="element-3827" data-mp="1k60br85fsre0">
                    <img src={person.src} alt={`${person.name}, ${person.role}`} className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover flex-shrink-0" style={{
                  objectPosition: person.position,
                  border: `3px solid ${BLUE}`
                }} data-id="element-3828" data-mp="1mo2e54jmjuxg" />
                    <div data-id="element-3829" data-mp="25fuxe78w61rz">
                      <p className="font-bebas text-2xl tracking-[0.04em] leading-tight" style={{
                    color: NAVY
                  }} data-id="element-3830" data-mp="25flf75u38ywu">
                        {person.name}
                      </p>
                      <p className="font-nunito text-sm leading-snug" style={{
                    color: 'rgba(11,31,63,0.6)'
                  }} data-id="element-3831" data-mp="20nl8mujij7ra">
                        {person.role}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* 3 — Pillars of Collaboration */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3832" data-mp="2z047bvit72bx">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3833" data-mp="2tzg5eet1l12x">
            <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-3834" data-mp="2u1j5xsrtmclw">
              <SectionLabel onDark data-id="element-3835" data-mp="2p03hrrucjpza">
                {isEs ? '01 — La Red' : '01 — The Network'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: '#ffffff'
            }} data-id="element-3836" data-mp="2k3d4fc9xf8hk">
                {isEs ? 'Pilares de Colaboración' : 'Pillars of Collaboration'}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-id="element-3837" data-mp="2680ye8qblvlc">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <motion.article key={pillar.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="relative pl-14" data-id="element-3838" data-mp="1wcpr2bh8rqnm" data-mpi={i}>
                    <span className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center font-bebas text-base tracking-wide text-white" style={{
                  backgroundColor: i % 2 === 0 ? SKY : 'rgba(255,255,255,0.14)'
                }} aria-hidden="true" data-id="element-3839" data-mp="13jcllrjb857x">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.6} data-id="element-3840" data-mp="pp362ut46sr3" />
                    <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-3 text-white" data-id="element-3841" data-mp="2mgplui33xbxt">
                      {isEs ? pillar.titleEs : pillar.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(255,255,255,0.68)'
                }} data-id="element-3842" data-mp="2wcsnlmu1r8k8">
                      {isEs ? pillar.textEs : pillar.text}
                    </p>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* 4 — Year One participation */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3843" data-mp="3f2s6tt37htfq">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3844" data-mp="1pp5bccybbs7c">
            <div className="max-w-2xl mb-14" data-id="element-3845" data-mp="1krdh3ca73tzz">
              <SectionLabel data-id="element-3846" data-mp="1ksokhazqazqk">
                {isEs ? '02 — Participación' : '02 — Participation'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3847" data-mp="b5pi85i4554">
                {isEs ? 'Cómo Se Ve la Participación en el Primer Año' : 'What Participation Can Look Like in Year One'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.7)'
            }} data-id="element-3848" data-mp="3dmo9v2ivxjoa">
                {isEs ? 'Construyendo conexiones, oportunidades e impacto compartido. Las organizaciones participantes podrán involucrarse de maneras que se alineen con su misión, capacidad y prioridades.' : 'Building connections, opportunities, and shared impact together. Participating organizations will have opportunities to engage in ways that align with their mission, capacity, and priorities.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3849" data-mp="15hdue8syr5qh">
              {PARTICIPATION.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="rounded-xl border bg-white p-7 h-full flex flex-col" style={{
                borderColor: 'rgba(11,31,63,0.12)'
              }} data-id="element-3850" data-mp="viev3gu38mg5" data-mpi={i}>
                    <Icon className="w-5 h-5 mb-4" style={{
                  color: BLUE
                }} strokeWidth={1.7} data-id="element-3851" data-mp="26xv56gb7yg5r" />
                    <h3 className="font-bebas text-xl tracking-[0.06em] uppercase mb-3" style={{
                  color: NAVY
                }} data-id="element-3852" data-mp="3ku4xuqhf5t53">
                      {isEs ? item.titleEs : item.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3853" data-mp="2ngsr0mdd5zf">
                      {isEs ? item.textEs : item.text}
                    </p>
                  </motion.div>;
            })}
            </div>

            <p className="font-nunito text-sm sm:text-base leading-relaxed max-w-3xl mt-10" style={{
            color: 'rgba(11,31,63,0.68)'
          }} data-id="element-3854" data-mp="hdfokwqgc5bq">
              {isEs ? 'La Red está diseñada para ser flexible. Las organizaciones pueden participar en las oportunidades más relevantes para su trabajo y capacidad. Nuestra meta es facilitar que las organizaciones se conecten, colaboren, compartan recursos y persigan oportunidades juntas.' : 'The Network is designed to be flexible. Organizations may participate in the opportunities most relevant to their work and capacity. Our goal is to make it easier for organizations to connect, collaborate, share resources, and pursue opportunities together.'}
            </p>
          </div>
        </section>

        {/* 5 — How do I join */}
        <section className="bg-white" data-id="element-3855" data-mp="3jlskoft1rrpw">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3856" data-mp="3epbttdugqz94">
            <div className="text-center mb-14" data-id="element-3857" data-mp="3sg6vo66jog37">
              <SectionLabel data-id="element-3858" data-mp="3sh07vmo9vxsq">
                {isEs ? '03 — Membresía' : '03 — Membership'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: NAVY
            }} data-id="element-3859" data-mp="3no716t98ufe0">
                {isEs ? '¿Cómo Me Uno?' : 'How Do I Join?'}
              </h2>
            </div>

            <ol className="space-y-10" data-id="element-3860" data-mp="2lu431ehcljot">
              {JOIN_STEPS.map((step, i) => <motion.li key={step.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="relative pl-16 sm:pl-20 pb-10 border-b last:border-b-0 last:pb-0" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3861" data-mp="2qlm380bjrg8q" data-mpi={i}>
                  <span className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center font-bebas text-lg tracking-wide text-white" style={{
                backgroundColor: i === 1 ? NAVY : BLUE
              }} aria-hidden="true" data-id="element-3862" data-mp="2zls9b14x1dxv">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-bebas text-3xl tracking-[0.04em] mb-4" style={{
                color: BLUE
              }} data-id="element-3863" data-mp="o02jukffnreg">
                    {isEs ? step.titleEs : step.title}
                  </h3>
                  <p className="font-nunito text-base leading-relaxed" style={{
                color: 'rgba(11,31,63,0.78)'
              }} data-id="element-3864" data-mp="jzio9laicvuf">
                    {isEs ? step.leadEs : step.lead}
                  </p>
                  {(isEs ? step.pointsEs : step.points).length > 0 ? <ul className="mt-5 space-y-3" data-id="element-3865" data-mp="3sherobap3kbz">
                      {(isEs ? step.pointsEs : step.points).map(point => <li key={point.slice(0, 40)} className="flex gap-3" data-id="element-3866" data-mp="a89qami0gxw">
                          <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0" style={{
                    color: RED
                  }} strokeWidth={2.5} data-id="element-3867" data-mp="3iifrzi7wqz14" />
                          <span className="font-nunito text-sm leading-relaxed" style={{
                    color: 'rgba(11,31,63,0.68)'
                  }} data-id="element-3868" data-mp="33x1xtkmr21ny">
                            {point}
                          </span>
                        </li>)}
                    </ul> : null}
                  {i === 0 ? <a href={joinHref} className="inline-flex items-center gap-2 mt-5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                color: BLUE
              }} data-id="element-3869" data-mp="3huzy5m48jxkg">
                      <MailIcon className="w-3.5 h-3.5" data-id="element-3870" data-mp="9f6j7bpjyu5i" />
                      {isEs ? 'Solicitar información' : 'Request information'}
                    </a> : null}
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* 6 — Map */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3871" data-mp="hfw3rmds9o1x">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3872" data-mp="hge6n1uyb0it">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3873" data-mp="mdxv0uld4toh">
              <div className="max-w-xl" data-id="element-3874" data-mp="r5xqa9qhmyeq">
                <SectionLabel onDark data-id="element-3875" data-mp="r70fmd85wssj">
                  {isEs ? '04 — Alcance' : '04 — Reach'}
                </SectionLabel>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] text-white" data-id="element-3876" data-mp="106drfcjrdtgf">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-white/50 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3877" data-mp="1a4a3q7ofmug4">
                {SISTER_ORGANIZATIONS.length > 0 ? isEs ? 'Seleccione un punto para ver la organización' : 'Select a point to view the organization' : isEs ? 'Las organizaciones aparecerán aquí conforme se unan' : 'Organizations will appear here as they join'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/12" style={{
            backgroundColor: NAVY_DEEP
          }} data-id="element-3878" data-mp="1k7p4mlhrz4wx">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10" data-id="element-3879" data-mp="1f7km6pxb4042">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3880" data-mp="2bxbr7j3855o4" />
                <span className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3881" data-mp="22y6t2dvkrech">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              <div className="h-[440px] sm:h-[560px] w-full" data-id="element-3882" data-mp="rkm81ywnt49b">
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center" style={{
                backgroundColor: NAVY_DEEP
              }} data-id="element-3884" data-mp="mjf1222cc93p">
                      <p className="font-nunito text-white/40 text-xs uppercase tracking-[0.2em]" data-id="element-3885" data-mp="2f9pd97zwuqcw">
                        {isEs ? 'Cargando mapa…' : 'Loading map…'}
                      </p>
                    </div>} data-id="element-3883" data-mp="mkzshitczbxc">
                  <NetworkMap isEs={isEs} data-id="element-3886" data-mp="2u5onao6kz0ko" />
                </Suspense>
              </div>
            </div>
          </div>
        </section>

        {/* 7 — Roster (renders once organizations are added) */}
        {SISTER_ORGANIZATIONS.length > 0 ? <section className="bg-white" data-id="element-3887" data-mp="2k6zd7hkxekez">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3888" data-mp="2p627q83a588p">
            <div className="max-w-2xl mb-12" data-id="element-3889" data-mp="2p8589m226jro">
              <SectionLabel data-id="element-3890" data-mp="18r0wqsnf4w3q">
                {isEs ? '05 — Organizaciones Participantes' : '05 — Participating Organizations'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3891" data-mp="166uuqqid7fpt">
                {isEs ? 'Quiénes Forman la Red' : 'Who Makes Up the Network'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.68)'
            }} data-id="element-3892" data-mp="1a97y589n31jf">
                {isEs ? 'Cada organización aporta una fortaleza distinta: educación, salud mental, artes, defensa legal, desarrollo juvenil. Esta es la base de la Red, y continúa creciendo.' : 'Each organization brings a distinct strength — education, mental health, the arts, legal advocacy, youth development. This is the foundation of the Network, and it continues to grow.'}
              </p>
            </div>

            <ul className="border-t" style={{
            borderColor: 'rgba(11,31,63,0.12)'
          }} data-id="element-3893" data-mp="3uzq9zuji1yh2">
              {SISTER_ORGANIZATIONS.map((org, i) => <motion.li key={org.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="border-b transition-colors duration-200 hover:bg-[#f5f8fc]" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3894" data-mp="1yuhgm2i1wt9e" data-mpi={i}>
                  <div className="py-8 grid lg:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.15fr)_auto] gap-x-8 gap-y-4 items-start px-2 sm:px-4" data-id="element-3895" data-mp="1pu2nigyui9vc">
                    <span className="font-nunito text-[10px] font-bold tabular-nums tracking-[0.1em] pt-2" style={{
                  color: 'rgba(11,31,63,0.35)'
                }} data-id="element-3896" data-mp="31b2mfvfp7myc">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-3897" data-mp="pnpdeuya4j0b">
                      <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-2.5" style={{
                    color: BLUE
                  }} data-id="element-3898" data-mp="pn7bbhoxrakh">
                        {isEs ? org.focusEs : org.focus}
                      </p>
                      <h3 className="font-bebas text-2xl sm:text-[1.7rem] tracking-[0.03em] leading-tight mb-2" style={{
                    color: NAVY
                  }} data-id="element-3899" data-mp="az2oq2mriewf">
                        {org.name}
                      </h3>
                      <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
                    color: 'rgba(11,31,63,0.55)'
                  }} data-id="element-3900" data-mp="1zpn1w4vqum9">
                        <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                      color: RED
                    }} data-id="element-3901" data-mp="1rc9j2fydxl34" />
                        {org.location}
                      </p>
                    </div>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3902" data-mp="158mntqsk0ttm">
                      {isEs ? org.descriptionEs : org.description}
                    </p>
                    <div className="flex flex-col gap-2.5 lg:items-end lg:text-right" data-id="element-3903" data-mp="hg5z4xeba3lc">
                      {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity whitespace-nowrap hover:opacity-70" style={{
                    color: BLUE
                  }} data-id="element-3904" data-mp="lgg8mk1bbc4y">
                          {isEs ? 'Sitio web' : 'Website'}
                          <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-3905" data-mp="3re7n53ldbm33" />
                        </a> : null}
                      <a href={mailto(`${isEs ? 'Contacto' : 'Contact'} — ${org.name}`)} className="inline-flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70 whitespace-nowrap" style={{
                    color: 'rgba(11,31,63,0.55)'
                  }} data-id="element-3906" data-mp="3md0hba2scwz2">
                        <MailIcon className="w-3.5 h-3.5" data-id="element-3907" data-mp="nw68oblebd3y" />
                        {isEs ? 'Contactar' : 'Contact'}
                      </a>
                    </div>
                  </div>
                </motion.li>)}
            </ul>
          </div>
        </section> : null}

        {/* 8 — CTA */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3908" data-mp="2kr2dd82w3g6k">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3909" data-mp="ja416og26nrz">
            <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[1.02] mb-7" data-id="element-3910" data-mp="eahr2nvj1nne">
              <span className="block" style={{
              color: SKY
            }} data-id="element-3911" data-mp="9jrfuu0f6qks">
                {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
              </span>
              <span className="block text-white" data-id="element-3912" data-mp="5hxml6sk0oun">
                {isEs ? 'Mayor Conexión' : 'Greater Connection'}
              </span>
              <span className="block" style={{
              color: RED
            }} data-id="element-3913" data-mp="3rpnq5b9p5k18">
                {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
              </span>
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-9" data-id="element-3914" data-mp="3msdsx9g2g42u">
              {isEs ? 'Queremos construir esto con ustedes, no simplemente para ustedes. Si su organización sirve a las comunidades hondureñas o hondureño-americanas, nos encantaría conocerla.' : 'We want to build this with you, not simply for you. If your organization serves Honduran or Honduran-American communities, we would be honored to know you.'}
            </p>
            <a href={joinHref} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3915" data-mp="33ry6cwr1g9ma">
              <MailIcon className="w-4 h-4" data-id="element-3916" data-mp="2tts58hd237ih" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white py-9 border-t" style={{
      borderColor: 'rgba(11,31,63,0.12)'
    }} data-id="element-3917" data-mp="95dw49pcz3jj">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3918" data-mp="258ilzkwtlbrg">
          <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-12 w-auto object-contain" data-id="element-3919" data-mp="217ynacwlm037" />
          <p className="font-nunito text-[10px] uppercase tracking-[0.22em]" style={{
          color: 'rgba(11,31,63,0.5)'
        }} data-id="element-3920" data-mp="2a7vgmrtxnn4l">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}